import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { IProvinces, ServiceTypes } from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';
import {
  Constants,
  nameLabels,
  telephoneInputMask,
  TextFieldVariants,
} from '../../../../helpers/Constants';
import strings from '../../../../localization';
import genericClasses from '../../../../theme/GenericClasses';
import styles from './style';

const salesForceKey = 'salesForce';

export const adminEditFormConfig = (provinces: IProvinces[]) => {
  const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

  const common = [
    {
      label: strings.noOfDaysToRatePharmacist,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      type: Constants.formInputType.text,
      name: 'ratePharmacistDays',
      xs: 6,
    },
    isSelfService && {
      label: strings.noOfDaysToRatePharmacy,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      type: Constants.formInputType.text,
      name: 'ratePharmacyDays',
      xs: 6,
    },
    {
      label: strings.minHoursWorkshift,
      type: Constants.formInputType.text,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      name: 'minHourWorkshift',
      xs: 6,
    },
    !isSelfService && {
      label: strings.formatString(
        strings.defaultHourlyRate,
        nameLabels().roleLabel,
      ),
      type: Constants.formInputType.text,
      name: 'pharmacistRate',
      xs: 6,
      suffix: '$',
      maxLength: 6,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
    },
    !isSelfService && {
      label: strings.formatString(strings.defaultHourlyRate, strings.pharmacy),
      type: Constants.formInputType.text,
      name: 'pharmacyRate',
      xs: 6,
      suffix: '$',
      maxLength: 6,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
    },
    !isSelfService && {
      label: strings.formatString(
        strings.defaultHourlyRate,
        `${strings.tech}.`,
      ),
      type: Constants.formInputType.text,
      name: 'technicianRate',
      xs: 6,
      suffix: '$',
      maxLength: 6,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
    },
    {
      label: strings.formatString(strings.userWeekdayRate, strings.professional) as string,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      type: Constants.formInputType.text,
      name: 'professionalWeekDayHourlyRate',
      xs: 6,
      suffix: '$',
      maxLength: 6,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.professional) as string,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      type: Constants.formInputType.text,
      name: 'professionalWeekendHourlyRate',
      suffix: '$',
      maxLength: 6,
      xs: 6,
    },
  ];

  const hourlyRate = [

    {
      label: strings.formatString(strings.userWeekdayRate, strings.pharmacy) as string,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      type: Constants.formInputType.text,
      suffix: '$',
      maxLength: 6,
      name: 'pharmacyWeekDayHourlyRate',
      xs: 6,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.pharmacy) as string,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      type: Constants.formInputType.text,
      suffix: '$',
      maxLength: 6,
      name: 'pharmacyWeekendHourlyRate',
      xs: 6,
    },
  ];

  const title = isSelfService ? strings.pharmagap : strings.pharmaPlan;

  const selfPreFormConfig = [
    {
      label: strings.formatString(strings.feesFormat, strings.unlimited),
      type: Constants.formInputType.text,
      name: 'unlimitedPackagePrice',
      xs: 6,
      suffix: '$',
      maxLength: 6,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
    },
    {
      label: strings.formatString(strings.feesFormat, strings.standard),
      type: Constants.formInputType.text,
      name: 'basicPackagePrice',
      xs: 6,
      suffix: '$',
      maxLength: 6,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
    },
  ];

  const selfPostFormConfig = [
    {
      label: strings.requestPendingPeriod,
      type: Constants.formInputType.text,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      name: 'bookingStateExpiration',
      xs: 6,
      suffix: strings.hoursAbbreviation,
    },

    {
      label: strings.pharmacyReminder,
      type: Constants.formInputType.text,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      name: 'pharmacyReminderBefore',
      xs: 6,
      suffix: strings.hoursAbbreviation,
    },
    {
      label: strings.tps,
      type: Constants.formInputType.text,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      name: 'tpsPercentage',
      xs: 6,
      suffix: '%',
    },
    {
      label: strings.tvq,
      type: Constants.formInputType.text,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      name: 'tvqPercentage',
      xs: 6,
      suffix: '%',
    },
    {
      label: strings.taxOnTax,
      type: Constants.formInputType.checkbox,
      name: 'taxOnTax',
      id: 'taxOnTax',
      xs: 6,
      customClass: styles.checkbox,
      labelClass: styles.checkboxText,
    },
  ];

  const formConfig = () =>
    (isSelfService
      ? [...selfPreFormConfig, ...common, ...selfPostFormConfig]
      : [...common, ...hourlyRate]);

  const pharmagapAccountSettings = {
    key: -1,
    title: strings.accountSettings,
    form: () =>
      [
        {
          label: strings.companyName,
          type: Constants.formInputType.text,
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          name: 'companyName',
          id: 'companyName',
          xs: 6,
        },
        {
          label: strings.address,
          type: Constants.formInputType.text,
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          name: 'address',
          id: 'address',
          xs: 6,
        },
        {
          label: strings.city,
          type: Constants.formInputType.text,
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          name: 'city',
          id: 'city',
          xs: 6,
        },
        {
          label: strings.province,
          type: Constants.formInputType.dropdown,
          menuItems: dropdownMapper(provinces ?? [], 'key', 'name'),
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          name: 'province',
          id: 'province',
          xs: 6,
        },
        {
          label: strings.phoneNumber,
          type: Constants.formInputType.masked,
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          name: 'phoneNumber',
          id: 'phoneNumber',
          mask: telephoneInputMask,
          xs: 6,
        },
        isSelfService && {
          label: strings.businessTPS,
          type: Constants.formInputType.text,
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          maxLength: 30,
          name: 'tpsNumber',
          id: 'tpsNumber',
          xs: 6,
        },
        isSelfService && {
          label: strings.businessTVQ,
          type: Constants.formInputType.text,
          customTextFieldStyle: genericClasses.customFilledInput,
          variant: TextFieldVariants.filled,
          name: 'tvqNumber',
          maxLength: 30,
          id: 'tvqNumber',
          xs: 6,
        },
      ],
  };

  let arr = [
    pharmagapAccountSettings,
    {
      key: 0,
      title: strings.formatString(strings.customSettings, title),
      form: formConfig,
    },
    {
      key: 1,
      title: strings.formatString(
        strings.customSettings,
        strings.customerServiceRepresentative,
      ),
      form: () =>
        [
          {
            label: strings.representativeName,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'representativeName',
            xs: 6,
          },
          {
            label: strings.representativeEmail,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'representativeEmail',
            xs: 6,
          },
          {
            label: strings.representativePhone,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'representativePhone',
            xs: 6,
          },
          {
            label: strings.phoneExt,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'representativePhoneExt',
            xs: 6,
          },
        ],
    },
    {
      key: salesForceKey,
      title: strings.formatString(strings.customSettings, strings.salesforce),
      form: () =>
        [
          {
            label: strings.salesForceUsername,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            type: Constants.formInputType.text,
            name: 'salesForceUserName',
            xs: 6,
          },
          {
            label: strings.salesForcePWD,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'salesForcePassword',
            xs: 6,
          },
          {
            label: strings.salesForceSecurityToken,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'salesForceSecurityToken',
            xs: 6,
          },
          {
            label: strings.urlOfSalesForce,
            type: Constants.formInputType.text,
            customTextFieldStyle: genericClasses.customFilledInput,
            variant: TextFieldVariants.filled,
            name: 'salesForceURL',
            xs: 6,
          },
          {
            label: strings.integrationSalesForce,
            type: Constants.formInputType.checkbox,
            name: 'salesForceActive',
            id: 'salesForceActive',
            xs: 6,
            customClass: styles.checkbox,
            labelClass: styles.checkboxText,
          },
        ],
    },
    {
      key: 4,
      title: strings.formatString(strings.customSettings, strings.more),
      form: () =>
        [
          {
            label: strings.sendAllEmailsBCC,
            type: Constants.formInputType.checkbox,
            name: 'envoiceBCCAdmin',
            id: 'envoiceBCCAdmin',
            xs: 6,
            customClass: styles.moreSettingsCheckbox,
            labelClass: styles.checkboxText,
          },
          {
            label: strings.showAllBookingDetails,
            type: Constants.formInputType.checkbox,
            name: 'showBookingDetails',
            id: 'showBookingDetails',
            xs: 6,
            customClass: styles.moreSettingsCheckbox,
            labelClass: styles.checkboxText,
          },
          {
            label: strings.enableChat,
            type: Constants.formInputType.checkbox,
            name: 'enableChat',
            id: 'enableChat',
            xs: 6,
            customClass: styles.moreSettingsCheckbox,
            labelClass: styles.checkboxText,
          },
        ],
    },
  ];

  if (isSelfService) {
    arr = arr.filter((item) =>
      item.key !== salesForceKey);
  }

  return arr;
};
